import { HoldingClass } from './HoldingClass';

/* eslint-disable no-use-before-define */
export type PartnerDraft = {
  name: string;
  cif: string;
  email: string;
  society: string;
  contribution: number;
  realContribution: number;
  discountPercent: number;
  discountByValuation: boolean;
  holdingClass: string;
  societyPartnerId: string;
  proportionality: string;
  proportionalityPercent: number;
  carriedShares: number;
  isHidden: boolean;
};

export type PartnerRepresentative = {
  name: string;
  email: string;
  initDate: Date;
  endDate: Date;
  authority: string;
  status: string;
  nif: string;
  nationality: string;
  address: string;
};

export type PartnerSharesCount = {
  actual: number;
  future: number;
  drafts: number;
  ranges?: number;
  coOwned?: number;
};

export type PartnerAddress = {
  line1: string;
  zip: string;
  city: string;
  state?: string;
  country: string;
};

export type RangeByClass = {
  class: string | HoldingClass;
  from: number;
  to: number;
};

export enum PartnerExtraFieldType {
  STRING = 'STRING', // eslint-disable-line no-unused-vars
  NUMBER = 'NUMBER', // eslint-disable-line no-unused-vars
  DATE = 'DATE', // eslint-disable-line no-unused-vars
  BOOLEAN = 'BOOLEAN', // eslint-disable-line no-unused-vars
}

export type PartnerExtraField = {
  labelKey: string;
  fieldType: PartnerExtraFieldType;
  value: string | number | boolean | Date;
  order?: number;
};

export type Partner = {
  _id: string;
  externalId?: string;
  cif: string;
  user: string;
  otherUsers: string[];
  readOnlyUsers: string[];
  email: string;
  emails: [{ text: string; category: string }];
  category: string;
  categories?: string[];
  name: string;
  address: {
    line1: string;
    zip: string;
    city: string;
    state: string;
    country: string;
  };
  phone: string;
  phoneSecondary: string;
  nationality: string;
  description: string;
  birthdate: Date;
  deceaseDate?: Date;
  image: string;
  registerDate: Date;
  isNaturalPerson: boolean;
  civilStatus: string;
  constitutionDocument: string;
  appointmentDocument: string;
  ownershipDocument: string;
  language: string;
  legalInfo: {
    register: string;
    section: string;
    sheet: string;
  };
  representative: PartnerRepresentative;
  incorporationDate: Date;
  shares: string[];
  nominalValue?: number;
  premium?: number;
  operations: string[];
  documents: string[];
  partnerNumber: number;
  isActive: boolean;
  sindication: string | null;
  sindicationRef: string | null;
  sindicationType: PartnerSindication | null;
  sindicationDocument: string | null;
  employeeNumber: string;
  taxAddress: PartnerAddress;
  bankAccountNumber: string;
  isNotDiluted: boolean;
  sharesCount: PartnerSharesCount;
  sharesBurdenCount: {
    coOwnerPrimary: number;
    coOwnerSecondary: number;
    usufruct: number;
    otherBurdens: number;
  };
  dividends: [
    {
      amount: number;
      retentionAmount: number;
      retentionPercentage: number;
      ndPercentage: number;
      operation: string;
    }
  ];
  contributions: [
    {
      amount: number;
      ndPercentage: number;
      operation: string;
    }
  ];
  splits: [
    {
      sharesCount: number;
      newSharesCount: number;
      operation: string;
    }
  ];

  renumerations: [
    {
      rangeByClass: RangeByClass[];
      operation: string;
    }
  ];

  configuration?: {
    equityMarket?: {
      enabled?: boolean;
    };
  };

  extraFields?: PartnerExtraField[];

  society: string;
};

export type PartnerCategory = {
  _id: string;
  name: string;
  description: string;
  society: string;
  createdAt?: Date;
  style: string;
  overridePartner?: boolean;
  partnersCount?: {
    total: number;
  };
  isDefault?: boolean;
};

export type PartnerSindication = 'STANDARD' | 'FULL';
